/**
 * Client-side date utilities for LA Mirage wedding platform
 *
 * CRITICAL: All wedding operations run in IST (Asia/Kolkata).
 * NEVER use `new Date(x).toISOString().split('T')[0]` on a wedding date — it converts
 * to UTC first, shifting IST dates back by 5.5 hours. Dates at 00:00 IST become the
 * PREVIOUS day in UTC, causing wedding-date mismatches and double-booking bugs.
 *
 * Use `toLocalDateString()` below for any wedding/event date that will be
 * compared, stored, or sent to the backend.
 */

export const IST_TIMEZONE = 'Asia/Kolkata';

/**
 * Convert a Date/string to YYYY-MM-DD in IST.
 *
 * Handles three common input shapes:
 *   1. Plain date string "2026-08-22" → returned as-is.
 *   2. ISO timestamp "2026-08-22T00:00:00.000Z" → formatted in IST (not UTC).
 *   3. Date object → formatted in IST.
 *
 * Returns empty string for null/undefined/invalid input.
 */
export function toLocalDateString(date: Date | string | null | undefined): string {
  if (!date) return '';

  // If already a plain YYYY-MM-DD string, return as-is (Postgres `date` columns
  // come back this way via Drizzle).
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return '';

  // Format via en-CA which uses YYYY-MM-DD natively and respects timeZone.
  return dateObj.toLocaleDateString('en-CA', { timeZone: IST_TIMEZONE });
}

/**
 * Today's date as YYYY-MM-DD in IST. Safe replacement for
 * `new Date().toISOString().split('T')[0]`, which is WRONG in IST between
 * 00:00 and 05:30 (returns yesterday's UTC date).
 */
export function todayLocalDateString(): string {
  return toLocalDateString(new Date());
}

/**
 * Format a date for human display in IST (e.g. "22 Aug 2026").
 */
export function formatDateDisplay(date: Date | string | null | undefined): string {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return '';
  return dateObj.toLocaleDateString('en-IN', {
    timeZone: IST_TIMEZONE,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Long human display in IST (e.g. "September 22, 2026"). For public/hero surfaces
 * (slideshows, catering, QR pages) that previously used date-fns 'MMMM d, yyyy' /
 * 'PPP' / 'PPPP' — which format in the viewer's local tz and can show the wrong day.
 * Pass withWeekday for "Saturday, September 22, 2026".
 */
export function formatDateLongDisplay(
  date: Date | string | null | undefined,
  withWeekday = false,
): string {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return '';
  return dateObj.toLocaleDateString('en-US', {
    timeZone: IST_TIMEZONE,
    ...(withWeekday ? { weekday: 'long' as const } : {}),
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * A clock time in IST, e.g. "5:40 PM". For "you can do this at …" copy on ops screens.
 * Explicit `timeZone` (not the istDateDefault patch), so it is IST in tests and in any
 * browser. Returns '' for null/invalid input — never a made-up time.
 */
export function formatTimeDisplay(date: Date | string | null | undefined): string {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return '';
  return dateObj.toLocaleTimeString('en-US', {
    timeZone: IST_TIMEZONE,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}
